/**
 * Iter 130 — Transfer Clinic Ownership Wizard.
 *
 * The clinic owner (`clinics.owner_user_id`) can hand ownership to an
 * existing admin or invite a brand-new admin by email. Renders a shadcn
 * AlertDialog with two tabs:
 *   1. Pick an existing admin → immediate transfer.
 *   2. Invite a new admin by email → magic-link invite created; ownership
 *      does not move until the new admin has accepted the invite.
 *
 * Guardrails on the backend:
 *   • Only the current owner can hit either endpoint.
 *   • Direct transfer requires role='admin' + active + not scheduled for
 *     deletion.
 *   • Invite creation writes an `invites` row scoped to the clinic.
 *
 * Props:
 *   open        bool          — controlled visibility
 *   onOpenChange (bool)=>void  — controlled setter
 *   onTransferred ()=>void     — called after a successful direct transfer
 */
import React, { useEffect, useState } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from './ui/alert-dialog';
import { Building2, UserCog, Mail, Copy, Check, X, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

export function TransferOwnershipWizard({ open, onOpenChange, onTransferred }) {
  const [tab, setTab] = useState('existing');
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);      // { clinic_id, clinic_name, candidates }
  const [selected, setSelected] = useState(''); // chosen candidate user_id
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [note, setNote] = useState('');
  const [inviteResult, setInviteResult] = useState(null); // response after invite

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setSelected('');
    setInviteEmail('');
    setInviteName('');
    setNote('');
    setInviteResult(null);
    api.get('/clinic/me/ownership/candidates')
      .then(r => {
        setMeta(r.data);
        // Auto-pick single-candidate scenarios for speed.
        if (r.data.candidates?.length === 1) {
          setSelected(r.data.candidates[0].user_id);
        }
      })
      .catch(e => {
        toast.error(e?.response?.data?.detail || 'Failed to load candidates.');
        onOpenChange(false);
      });
  }, [open, onOpenChange]);

  const transferToExisting = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post('/clinic/me/ownership/transfer', {
        new_owner_user_id: selected,
        note: note || null,
      });
      toast.success(data.message || 'Ownership transferred.', { duration: 7000 });
      onTransferred?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Transfer failed.');
    } finally {
      setBusy(false);
    }
  };

  const inviteNewAdmin = async () => {
    if (!inviteEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post('/clinic/me/ownership/transfer', {
        invite_email: inviteEmail.trim().toLowerCase(),
        invite_name: inviteName.trim() || null,
        note: note || null,
      });
      setInviteResult(data);
      toast.success('Invite created.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Invite failed.');
    } finally {
      setBusy(false);
    }
  };

  const copyInviteCode = () => {
    if (!inviteResult?.invite_code) return;
    try {
      navigator.clipboard.writeText(inviteResult.invite_code);
      toast.success('Invite code copied.');
    } catch (_) {
      toast.error('Copy failed. Select the code manually to copy.');
    }
  };

  const cands = meta?.candidates || [];
  const canDirectTransfer = !!selected && !busy && cands.length > 0;
  const canInvite = !!inviteEmail.trim() && !busy && !inviteResult;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg" data-testid="ownership-wizard-modal">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 text-fg-muted hover:text-fg"
          aria-label="Close"
          data-testid="ownership-wizard-close"
        >
          <X className="w-5 h-5"/>
        </button>
        <AlertDialogHeader>
          <div
            className="flex items-center gap-3 -mx-6 -mt-6 mb-2 px-6 py-4"
            style={{
              background: 'linear-gradient(90deg, #E8F0FA 0%, #F2EBDB 100%)',
              borderBottom: '1px solid #B4C4DA',
            }}
          >
            <div className="w-11 h-11 rounded-full bg-[#003D80]/10 text-[#003D80] grid place-items-center shrink-0">
              <ShieldCheck className="w-6 h-6"/>
            </div>
            <div>
              <AlertDialogTitle className="text-lg font-heading font-bold text-[#002B5C]">
                Transfer clinic ownership
              </AlertDialogTitle>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#003D80]/70 mt-0.5 inline-flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5"/>
                {meta?.clinic_name || 'Your clinic'}
              </div>
            </div>
          </div>

          <AlertDialogDescription asChild>
            <div className="space-y-4 text-sm">
              <p className="text-fg-muted">
                As the clinic owner, you handle billing, tier, and admin lifecycle.
                Before you can delete or deactivate your own account, hand ownership
                to another admin so your clinic isn't left without a legal contact.
              </p>

              {/* Tab switcher */}
              <div className="flex gap-1 border-b border-soft" data-testid="ownership-wizard-tabs">
                <button
                  type="button"
                  onClick={() => setTab('existing')}
                  className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px ${
                    tab === 'existing'
                      ? 'border-brand text-brand'
                      : 'border-transparent text-fg-muted hover:text-fg'
                  }`}
                  data-testid="ownership-wizard-tab-existing"
                >
                  <UserCog className="w-3.5 h-3.5 inline mr-1 -mt-0.5"/>
                  Existing admin
                </button>
                <button
                  type="button"
                  onClick={() => setTab('invite')}
                  className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px ${
                    tab === 'invite'
                      ? 'border-brand text-brand'
                      : 'border-transparent text-fg-muted hover:text-fg'
                  }`}
                  data-testid="ownership-wizard-tab-invite"
                >
                  <Mail className="w-3.5 h-3.5 inline mr-1 -mt-0.5"/>
                  Invite new admin
                </button>
              </div>

              {tab === 'existing' && (
                <div className="space-y-3" data-testid="ownership-wizard-existing-panel">
                  {!meta ? (
                    <div className="text-xs text-fg-muted">Loading candidates…</div>
                  ) : cands.length === 0 ? (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="ownership-wizard-no-candidates">
                      <div className="font-semibold mb-1">No eligible admins on this clinic.</div>
                      <div>
                        You&apos;re the only admin on <strong>{meta?.clinic_name}</strong>.
                        Switch to the <em>Invite new admin</em> tab to send a magic-link invite,
                        then finish the transfer once they accept.
                      </div>
                    </div>
                  ) : (
                    <>
                      <label className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted block">
                        Pick the new owner ({cands.length} eligible admin{cands.length === 1 ? '' : 's'})
                      </label>
                      <div className="space-y-1.5 max-h-52 overflow-auto" data-testid="ownership-wizard-candidates">
                        {cands.map(c => (
                          <label
                            key={c.user_id}
                            className={`flex items-start gap-3 px-3 py-2 rounded-md border cursor-pointer transition ${
                              selected === c.user_id
                                ? 'border-brand bg-[#F2EBDB]'
                                : 'border-soft hover:border-brand/60'
                            }`}
                            data-testid={`ownership-wizard-candidate-${c.user_id}`}
                          >
                            <input
                              type="radio"
                              name="new-owner"
                              className="mt-1 accent-brand"
                              value={c.user_id}
                              checked={selected === c.user_id}
                              onChange={() => setSelected(c.user_id)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{c.name}</div>
                              <div className="text-[11px] text-fg-muted truncate">{c.email}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                      <label className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted block mt-3">
                        Handover note (optional, saved to the audit log)
                      </label>
                      <textarea
                        className="input w-full text-sm min-h-[56px]"
                        placeholder="Anything the new owner should know?"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        maxLength={400}
                        data-testid="ownership-wizard-note"
                      />
                    </>
                  )}
                </div>
              )}

              {tab === 'invite' && (
                <div className="space-y-3" data-testid="ownership-wizard-invite-panel">
                  {inviteResult ? (
                    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 space-y-3" data-testid="ownership-wizard-invite-success">
                      <div className="flex items-center gap-2 text-emerald-800 text-sm font-semibold">
                        <Check className="w-4 h-4"/> Invite created for {inviteResult.invite_email}
                      </div>
                      <p className="text-xs text-emerald-900">
                        Share this invite code with your new admin. They&apos;ll sign up as
                        an admin on your clinic. Once they accept, come back here and
                        finish the transfer from the <em>Existing admin</em> tab.
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-sm font-mono bg-white border border-emerald-200 rounded px-3 py-2 tracking-wider" data-testid="ownership-wizard-invite-code">
                          {inviteResult.invite_code}
                        </code>
                        <button
                          type="button"
                          onClick={copyInviteCode}
                          className="btn-secondary text-xs inline-flex items-center gap-1"
                          data-testid="ownership-wizard-copy-code"
                        >
                          <Copy className="w-3.5 h-3.5"/> Copy
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <label className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted block">
                        New admin email <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="email"
                        className="input w-full text-sm"
                        placeholder="new-owner@clinic.com"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        data-testid="ownership-wizard-invite-email"
                        autoComplete="email"
                      />
                      <label className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted block mt-2">
                        Full name (optional)
                      </label>
                      <input
                        type="text"
                        className="input w-full text-sm"
                        placeholder="Dr. Jane Doe"
                        value={inviteName}
                        onChange={e => setInviteName(e.target.value)}
                        data-testid="ownership-wizard-invite-name"
                        autoComplete="name"
                      />
                      <label className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted block mt-2">
                        Handover note (optional)
                      </label>
                      <textarea
                        className="input w-full text-sm min-h-[56px]"
                        placeholder="Context for the new owner"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        maxLength={400}
                        data-testid="ownership-wizard-invite-note"
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="ownership-wizard-cancel">
            Not now
          </AlertDialogCancel>
          {tab === 'existing' ? (
            <AlertDialogAction
              onClick={transferToExisting}
              disabled={!canDirectTransfer}
              className="bg-[#003D80] hover:bg-[#002B5C] focus:ring-[#003D80] text-white disabled:opacity-40"
              data-testid="ownership-wizard-confirm-transfer"
            >
              <ShieldCheck className="w-4 h-4 mr-1.5"/>
              Transfer ownership
            </AlertDialogAction>
          ) : inviteResult ? (
            <AlertDialogAction
              onClick={() => onOpenChange(false)}
              className="bg-[#003D80] hover:bg-[#002B5C] focus:ring-[#003D80] text-white"
              data-testid="ownership-wizard-done"
            >
              Done
            </AlertDialogAction>
          ) : (
            <AlertDialogAction
              onClick={inviteNewAdmin}
              disabled={!canInvite}
              className="bg-[#003D80] hover:bg-[#002B5C] focus:ring-[#003D80] text-white disabled:opacity-40"
              data-testid="ownership-wizard-confirm-invite"
            >
              <Mail className="w-4 h-4 mr-1.5"/>
              Send invite
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
